/* Fusion Prints – scroll-driven 3D stand assembly. Plain WebGL2, no libraries. */
(function () {
  'use strict';

  // ---------- small math helpers (column-major 4x4) ----------
  const M = {
    ident() { return new Float32Array([1,0,0,0, 0,1,0,0, 0,0,1,0, 0,0,0,1]); },
    mul(a, b) {
      const o = new Float32Array(16);
      for (let c = 0; c < 4; c++) for (let r = 0; r < 4; r++) {
        o[c*4+r] = a[r]*b[c*4] + a[4+r]*b[c*4+1] + a[8+r]*b[c*4+2] + a[12+r]*b[c*4+3];
      }
      return o;
    },
    persp(fovy, asp, n, f) {
      const t = 1 / Math.tan(fovy / 2), o = new Float32Array(16);
      o[0] = t / asp; o[5] = t; o[10] = (f + n) / (n - f); o[11] = -1; o[14] = 2 * f * n / (n - f);
      return o;
    },
    ortho(l, r, b, t, n, f) {
      const o = M.ident();
      o[0] = 2/(r-l); o[5] = 2/(t-b); o[10] = -2/(f-n);
      o[12] = -(r+l)/(r-l); o[13] = -(t+b)/(t-b); o[14] = -(f+n)/(f-n);
      return o;
    },
    lookAt(e, c, up) {
      let zx = e[0]-c[0], zy = e[1]-c[1], zz = e[2]-c[2];
      let l = Math.hypot(zx, zy, zz); zx/=l; zy/=l; zz/=l;
      let xx = up[1]*zz - up[2]*zy, xy = up[2]*zx - up[0]*zz, xz = up[0]*zy - up[1]*zx;
      l = Math.hypot(xx, xy, xz); xx/=l; xy/=l; xz/=l;
      const yx = zy*xz - zz*xy, yy = zz*xx - zx*xz, yz = zx*xy - zy*xx;
      return new Float32Array([xx,yx,zx,0, xy,yy,zy,0, xz,yz,zz,0,
        -(xx*e[0]+xy*e[1]+xz*e[2]), -(yx*e[0]+yy*e[1]+yz*e[2]), -(zx*e[0]+zy*e[1]+zz*e[2]), 1]);
    },
    trans(x, y, z) { const o = M.ident(); o[12]=x; o[13]=y; o[14]=z; return o; },
    rotY(a) { const c=Math.cos(a), s=Math.sin(a), o=M.ident(); o[0]=c; o[2]=-s; o[8]=s; o[10]=c; return o; },
    rotX(a) { const c=Math.cos(a), s=Math.sin(a), o=M.ident(); o[5]=c; o[6]=s; o[9]=-s; o[10]=c; return o; },
    rotZ(a) { const c=Math.cos(a), s=Math.sin(a), o=M.ident(); o[0]=c; o[1]=s; o[4]=-s; o[5]=c; return o; },
  };
  const clamp = (x, a, b) => Math.min(b, Math.max(a, x));
  const seg = (p, a, b) => clamp((p - a) / (b - a), 0, 1);
  const ease = t => t < .5 ? 4*t*t*t : 1 - Math.pow(-2*t + 2, 3) / 2;      // easeInOutCubic
  const easeOut = t => 1 - Math.pow(1 - t, 3);
  const lerp = (a, b, t) => a + (b - a) * t;

  // ---------- model space → display space ----------
  // STL is in print orientation: side profile in XY, width along Z, base face at y = -451.47.
  // Display: X = along the stand, Y = up (away from base), Z = across the width.
  const BASE_Y = -451.47, CX = -793.4, CZ = 25, CY = 88;
  const toDisp = (x, y, z) => [x - CX, (BASE_Y - y) - CY, z - CZ];

  // ---------- all 12 grip pockets measured from the STL (mouth centre, depth, outward axis in model space) ----------
  const SLOPE_N = [-0.342421, -0.939490, 0];          // outward normal of the 20° top face
  const LIP_N = [0.937393, -0.348310, 0];            // outward normal of the front lip's inner face
  const POCKETS = [
    // part 0 (front piece)
    { part: 0, c: [-951.57, -451.47,  7.75], d: 1.0, n: [0, 1, 0] },
    { part: 0, c: [-951.57, -451.47, 42.25], d: 1.0, n: [0, 1, 0] },
    { part: 0, c: [-762.57, -451.47, 25.00], d: 1.3, n: [0, 1, 0] },
    { part: 0, c: [-772.90, -451.47, 68.25], d: 1.3, n: [0, 1, 0] },
    // part 1 (rear piece)
    { part: 1, c: [-740.50, -451.47, 25.00], d: 1.3, n: [0, 1, 0] },
    { part: 1, c: [-727.19, -451.47, 68.25], d: 1.3, n: [0, 1, 0] },
    { part: 1, c: [-629.50, -451.47,  7.75], d: 1.0, n: [0, 1, 0] },
    { part: 1, c: [-629.52, -451.47, 42.25], d: 1.0, n: [0, 1, 0] },
    { part: 1, c: [-627.765, -616.148,  7.748], d: 0.99, n: SLOPE_N },
    { part: 1, c: [-627.765, -616.149, 42.25 ], d: 0.99, n: SLOPE_N },
    // part 0: inner face of the front lip (the controller rests against it)
    { part: 0, c: [-988.974, -551.965,  7.748], d: 0.9, n: LIP_N },
    { part: 0, c: [-988.974, -551.965, 42.252], d: 0.9, n: LIP_N },
  ];
  // display-space version of a model direction
  const dirToDisp = v => [v[0], -v[1], v[2]];

  // ---------- geometry ----------
  async function decode(b64) {
    const bin = Uint8Array.from(atob(b64), ch => ch.charCodeAt(0));
    let buf;
    if ('DecompressionStream' in window) {
      const ds = new DecompressionStream('deflate');
      buf = await new Response(new Blob([bin]).stream().pipeThrough(ds)).arrayBuffer();
    } else { throw new Error('no DecompressionStream'); }
    const dv = new DataView(buf);
    const mn = [dv.getFloat32(0, true), dv.getFloat32(4, true), dv.getFloat32(8, true)];
    const sc = dv.getFloat32(12, true);
    let off = 16; const parts = [];
    for (let p = 0; p < 2; p++) {
      const nv = dv.getUint32(off, true), ni = dv.getUint32(off + 4, true); off += 8;
      const q = new Uint16Array(buf, off, nv * 3); off += nv * 6;
      const idx = new Uint16Array(buf.slice(off, off + ni * 6)); off += ni * 6;
      const pos = new Float32Array(nv * 3);
      for (let i = 0; i < nv; i++) {
        const d = toDisp(mn[0] + q[i*3] * sc, mn[1] + q[i*3+1] * sc, mn[2] + q[i*3+2] * sc);
        pos[i*3] = d[0]; pos[i*3+1] = d[1]; pos[i*3+2] = d[2];
      }
      parts.push(creased(pos, idx, 32));
    }
    return parts;
  }

  // Expand an indexed mesh into per-corner normals, smoothing only across edges sharper than `deg`.
  function creased(pos, idx, deg) {
    const nf = idx.length / 3, fn = new Float32Array(nf * 3);
    const vf = new Map();
    for (let f = 0; f < nf; f++) {
      const a = idx[f*3], b = idx[f*3+1], c = idx[f*3+2];
      const ux = pos[b*3]-pos[a*3], uy = pos[b*3+1]-pos[a*3+1], uz = pos[b*3+2]-pos[a*3+2];
      const vx = pos[c*3]-pos[a*3], vy = pos[c*3+1]-pos[a*3+1], vz = pos[c*3+2]-pos[a*3+2];
      // display transform flips Y (mirror), so swap winding to keep outward normals
      let nx = -(uy*vz - uz*vy), ny = -(uz*vx - ux*vz), nz = -(ux*vy - uy*vx);
      fn[f*3] = nx; fn[f*3+1] = ny; fn[f*3+2] = nz;              // area weighted (unnormalised)
      for (const v of [a, b, c]) { let l = vf.get(v); if (!l) vf.set(v, l = []); l.push(f); }
    }
    const cosT = Math.cos(deg * Math.PI / 180);
    const P = new Float32Array(nf * 9), N = new Float32Array(nf * 9);
    const unit = f => { const x = fn[f*3], y = fn[f*3+1], z = fn[f*3+2], l = Math.hypot(x, y, z) || 1; return [x/l, y/l, z/l]; };
    for (let f = 0; f < nf; f++) {
      const n0 = unit(f);
      for (let k = 0; k < 3; k++) {
        const v = idx[f*3+k];
        let sx = 0, sy = 0, sz = 0;
        for (const g of vf.get(v)) {
          const ng = unit(g);
          if (ng[0]*n0[0] + ng[1]*n0[1] + ng[2]*n0[2] >= cosT) { sx += fn[g*3]; sy += fn[g*3+1]; sz += fn[g*3+2]; }
        }
        const l = Math.hypot(sx, sy, sz) || 1;
        const o = (f*3 + k) * 3;
        P[o] = pos[v*3]; P[o+1] = pos[v*3+1]; P[o+2] = pos[v*3+2];
        N[o] = sx/l; N[o+1] = sy/l; N[o+2] = sz/l;
      }
    }
    return { P, N, count: nf * 3 };
  }

  // Rubber grip: a disc with a softly rounded top edge, local axis +Y, base at y = 0.
  function gripMesh(r, h, fillet, segs) {
    const prof = [[0, 0, 0, -1], [r, 0, 0, -1], [r, 0, 1, 0], [r, h - fillet, 1, 0]];
    for (let i = 1; i <= 6; i++) {
      const a = (i / 6) * Math.PI / 2;
      prof.push([r - fillet + Math.cos(a) * fillet, h - fillet + Math.sin(a) * fillet, Math.cos(a), Math.sin(a)]);
    }
    prof.push([0, h, 0, 1]);
    const P = [], N = [];
    for (let s = 0; s < segs; s++) {
      const a0 = s / segs * Math.PI * 2, a1 = (s + 1) / segs * Math.PI * 2;
      for (let j = 0; j < prof.length - 1; j++) {
        const A = prof[j], B = prof[j+1];
        if (Math.abs(A[0] - B[0]) < 1e-6 && Math.abs(A[1] - B[1]) < 1e-6) continue;
        const v = (p, a) => [p[0]*Math.cos(a), p[1], p[0]*Math.sin(a)];
        const n = (p, a) => [p[2]*Math.cos(a), p[3], p[2]*Math.sin(a)];
        const quad = [[A,a0],[B,a0],[B,a1],[A,a0],[B,a1],[A,a1]];
        // outward winding check via first face
        for (const [p, a] of quad) { P.push(...v(p, a)); N.push(...n(p, a)); }
      }
    }
    return { P: new Float32Array(P), N: new Float32Array(N), count: P.length / 3 };
  }

  // ---------- shaders ----------
  const VS = `#version 300 es
  in vec3 aPos; in vec3 aNor;
  uniform mat4 uModel, uVP, uLightVP;
  out vec3 vW; out vec3 vN; out vec3 vL; out vec4 vS;
  void main(){
    vec4 w = uModel * vec4(aPos,1.0);
    vW = w.xyz; vN = mat3(uModel) * aNor; vL = aPos;
    vS = uLightVP * w;
    gl_Position = uVP * w;
  }`;

  const FS = `#version 300 es
  precision highp float; precision highp sampler2DShadow;
  in vec3 vW; in vec3 vN; in vec3 vL; in vec4 vS;
  uniform vec3 uFillDir, uFillCol;
  uniform vec3 uCam, uBase, uKeyDir, uKeyCol, uRim1Dir, uRim1Col, uRim2Dir, uRim2Col;
  uniform vec3 uLayerAxis;       // print layer stacking direction in world space
  uniform float uRough, uAniso, uSpec, uFlash, uGrip;
  uniform sampler2DShadow uShadow;
  out vec4 frag;
  const float PI = 3.14159265;
  float shadowPCF(){
    vec3 p = vS.xyz / vS.w * 0.5 + 0.5;
    if (p.x<0.0||p.x>1.0||p.y<0.0||p.y>1.0) return 1.0;
    float s = 0.0; vec2 t = vec2(1.0/2048.0);
    for (int x=-2;x<=2;x++) for (int y=-2;y<=2;y++) s += texture(uShadow, vec3(p.xy + vec2(x,y)*t*1.2, p.z - 0.0012));
    return s / 25.0;
  }
  float D_GGX(float NoH, float a){ float a2=a*a; float d=NoH*NoH*(a2-1.0)+1.0; return a2/(PI*d*d); }
  float D_aniso(vec3 H, vec3 N, vec3 T, vec3 B, float ax, float ay){
    float ToH=dot(T,H), BoH=dot(B,H), NoH=dot(N,H);
    float d = ToH*ToH/(ax*ax) + BoH*BoH/(ay*ay) + NoH*NoH;
    return 1.0/(PI*ax*ay*d*d);
  }
  float V_smith(float NoV, float NoL, float a){ float k=a*0.5; return 0.25/((NoV*(1.0-k)+k)*(NoL*(1.0-k)+k)); }
  vec3 light(vec3 N, vec3 V, vec3 L, vec3 col, vec3 T, vec3 B, float sh){
    vec3 H = normalize(L+V);
    float NoL = max(dot(N,L),0.0), NoV = max(dot(N,V),1e-3), NoH = max(dot(N,H),0.0), VoH=max(dot(V,H),0.0);
    float a = uRough*uRough;
    float D = mix(D_GGX(NoH,a), D_aniso(H,N,T,B, a*(1.0+uAniso*2.2), a*max(0.08,1.0-uAniso*0.9)), uAniso>0.0?0.7:0.0);
    float F = uSpec + (1.0-uSpec)*pow(1.0-VoH,5.0);
    vec3 spec = vec3(D*V_smith(NoV,NoL,a)*F);
    vec3 diff = uBase/PI*(1.0-F);
    return (diff+spec)*col*NoL*sh;
  }
  vec3 aces(vec3 x){ return clamp((x*(2.51*x+0.03))/(x*(2.43*x+0.59)+0.14),0.0,1.0); }
  void main(){
    vec3 N = normalize(vN); vec3 V = normalize(uCam - vW);
    // tangent along print layers (layers stack along uLayerAxis, so lines run perpendicular to it)
    vec3 T = cross(uLayerAxis, N); float tl = length(T);
    T = tl>1e-3 ? T/tl : normalize(cross(vec3(0,1,0),N)+1e-4);
    vec3 Bt = normalize(cross(N,T));
    float sh = shadowPCF();
    vec3 c = vec3(0.0);
    c += light(N,V,normalize(uKeyDir), uKeyCol, T,Bt, sh);
    c += light(N,V,normalize(uRim1Dir), uRim1Col, T,Bt, 1.0);
    c += light(N,V,normalize(uRim2Dir), uRim2Col, T,Bt, 1.0);
    c += light(N,V,normalize(uFillDir), uFillCol, T,Bt, 1.0);
    // studio ambient: soft top light + floor bounce
    float up = N.y*0.5+0.5;
    vec3 amb = mix(vec3(0.020,0.021,0.030), vec3(0.090,0.092,0.115), up);
    c += uBase*amb*(0.55+0.45*sh) + amb*0.10*pow(1.0-max(dot(N,V),0.0),3.0);
    // fresnel rim glow in brand colours
    float fr = pow(1.0-max(dot(N,V),0.0), 4.0);
    c += mix(vec3(0.26,0.18,0.62), vec3(0.14,0.42,0.75), N.x*0.5+0.5) * fr * 0.07 * (1.0-uGrip*0.6);
    c += vec3(0.30,0.36,0.95)*uFlash;
    c = aces(c*1.35);
    frag = vec4(pow(c, vec3(1.0/2.2)), 1.0);
  }`;

  const SVS = `#version 300 es
  in vec3 aPos; uniform mat4 uModel, uLightVP;
  void main(){ gl_Position = uLightVP * uModel * vec4(aPos,1.0); }`;
  const SFS = `#version 300 es
  precision mediump float; out vec4 o; void main(){ o = vec4(1.0); }`;

  // ground: shadow catcher + soft glow, drawn with premultiplied alpha
  const GVS = `#version 300 es
  in vec2 aXZ; uniform mat4 uVP, uLightVP; uniform float uY, uSize;
  out vec4 vS; out vec2 vXZ;
  void main(){ vec4 w = vec4(aXZ.x*uSize, uY, aXZ.y*uSize, 1.0); vXZ=aXZ; vS = uLightVP*w; gl_Position = uVP*w; }`;
  const GFS = `#version 300 es
  precision highp float; precision highp sampler2DShadow;
  in vec4 vS; in vec2 vXZ; uniform sampler2DShadow uShadow; uniform float uFade; out vec4 o;
  void main(){
    vec3 p = vS.xyz/vS.w*0.5+0.5; float s=0.0; vec2 t=vec2(1.0/2048.0);
    for(int x=-3;x<=3;x++) for(int y=-3;y<=3;y++) s+=texture(uShadow, vec3(p.xy+vec2(x,y)*t*2.2, p.z-0.002));
    s/=49.0;
    float r = length(vXZ); float fall = smoothstep(1.0, 0.15, r);
    float a = (1.0-s)*0.62*fall*uFade;
    vec3 glow = mix(vec3(0.26,0.18,0.62), vec3(0.13,0.38,0.70), vXZ.x*0.5+0.5);
    float g = smoothstep(0.75,0.0,r)*0.10*uFade;
    o = vec4(glow*g*(1.0-a), a + g*0.6);
  }`;

  function compile(gl, vs, fs) {
    const mk = (t, s) => { const sh = gl.createShader(t); gl.shaderSource(sh, s); gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(sh)); return sh; };
    const p = gl.createProgram(); gl.attachShader(p, mk(gl.VERTEX_SHADER, vs)); gl.attachShader(p, mk(gl.FRAGMENT_SHADER, fs));
    gl.linkProgram(p); if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p));
    const u = {}; const n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
    for (let i = 0; i < n; i++) { const info = gl.getActiveUniform(p, i); u[info.name] = gl.getUniformLocation(p, info.name); }
    return { p, u };
  }

  function vao(gl, prog, mesh) {
    const v = gl.createVertexArray(); gl.bindVertexArray(v);
    const put = (name, data, size) => { const loc = gl.getAttribLocation(prog, name); if (loc < 0) return;
      const b = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, b); gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
      gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, size, gl.FLOAT, false, 0, 0); };
    put('aPos', mesh.P, 3); if (mesh.N) put('aNor', mesh.N, 3);
    gl.bindVertexArray(null); return v;
  }

  // ---------- the scene ----------
  async function init(root) {
    const canvas = root.querySelector('canvas');
    const dataEl = root.querySelector('[data-fp3d-src]');
    const gl = canvas.getContext('webgl2', { antialias: true, alpha: true, premultipliedAlpha: true });
    if (!gl) { root.classList.add('fp3d--nogl'); return; }

    const b64 = (dataEl && dataEl.dataset.fp3dSrc) || window.FP_STAND_B64 || (window.FP_STAND_D || []).join('');
    const parts = await decode(b64);

    const prog = compile(gl, VS, FS), sprog = compile(gl, SVS, SFS), gprog = compile(gl, GVS, GFS);
    const grip = gripMesh(6.6, 2.6, 0.9, 40);
    const meshes = parts.map(m => ({ m, v: vao(gl, prog.p, m), s: vao(gl, sprog.p, { P: m.P }) }));
    const gripV = vao(gl, prog.p, grip), gripS = vao(gl, sprog.p, { P: grip.P });
    const quad = gl.createVertexArray(); gl.bindVertexArray(quad);
    const qb = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, qb);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, 1,1, -1,-1, 1,1, -1,1]), gl.STATIC_DRAW);
    const gl0 = gl.getAttribLocation(gprog.p, 'aXZ'); gl.enableVertexAttribArray(gl0); gl.vertexAttribPointer(gl0, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);

    // grip placement matrices (relative to their part, in display space)
    const grips = POCKETS.map((pk, i) => {
      const n = dirToDisp(pk.n);                                        // outward axis in display space
      const c = toDisp(...pk.c);
      const base = [c[0] - n[0]*pk.d, c[1] - n[1]*pk.d, c[2] - n[2]*pk.d]; // pocket floor
      // rotation taking +Y onto n
      const ang = Math.acos(clamp(n[1], -1, 1));
      let R = M.ident();
      if (ang > Math.PI - 1e-4) {
        R = M.rotX(Math.PI);                                           // straight down: flip about X
      } else if (ang > 1e-4) {
        // axis = Y × n = (n.z, 0, -n.x)
        const ax = [n[2], 0, -n[0]]; const l = Math.hypot(ax[0], ax[2]);
        const x = ax[0]/l, z = ax[2]/l, cs = Math.cos(ang), sn = Math.sin(ang), t = 1 - cs;
        R = new Float32Array([t*x*x+cs, sn*z, t*x*z, 0,  -sn*z, cs, sn*x, 0,  t*x*z, -sn*x, t*z*z+cs, 0,  0,0,0,1]);
      }
      return { part: pk.part, n, base, R, order: i };
    });

    // shadow map
    const SM = 2048;
    const stex = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, stex);
    gl.texStorage2D(gl.TEXTURE_2D, 1, gl.DEPTH_COMPONENT24, SM, SM);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_COMPARE_MODE, gl.COMPARE_REF_TO_TEXTURE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_COMPARE_FUNC, gl.LEQUAL);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    const sfb = gl.createFramebuffer(); gl.bindFramebuffer(gl.FRAMEBUFFER, sfb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, stex, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    const KEY = [0.45, 1.0, 0.55];
    const lightVP = M.mul(M.ortho(-330, 330, -330, 330, 1, 2000), M.lookAt([KEY[0]*800, KEY[1]*800, KEY[2]*800], [0, -40, 0], [0, 1, 0]));

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let target = 0, shown = 0, w = 0, h = 0, raf = 0, t0 = performance.now();

    function progress() {
      const r = root.getBoundingClientRect();
      const total = r.height - window.innerHeight;
      return total > 0 ? clamp(-r.top / total, 0, 1) : 0;
    }

    // ---- choreography: everything is a pure function of scroll progress p ----
    function pose(p, time) {
      const pA = seg(p, 0.00, 0.40);   // spin while apart, lining up
      const pB = seg(p, 0.40, 0.62);   // dovetails slide together across the width
      const pC = seg(p, 0.62, 0.86);   // flip to show the base, grips press in
      const pD = seg(p, 0.86, 1.00);   // settle to hero angle
      const sep = 1 - ease(pB);
      const drift = 1 - ease(pA);
      const part = [
        { x: -38*drift, y: 26*drift, z: 78*sep + 20*drift, rz: 0.10*drift },
        { x:  38*drift, y: -18*drift, z: -78*sep - 20*drift, rz: -0.08*drift },
      ];
      // whole-stand rotation: ~1.5 turns while assembling, settling at a 3/4 hero view
      const yawEnd = -0.80;
      const spin = (1 - easeOut(seg(p, 0, 0.86))) * Math.PI * 3.0;
      const yaw = yawEnd + spin ;
      const hold = ease(seg(pC, 0, 0.28)) * (1 - ease(seg(pC, 0.78, 1)));   // tilt up, hold, tilt back
      const flip = hold * 1.25;          // tilt to reveal the underside, then back
      const pitch = 0.26 - flip;
      const grip = seg(pC, 0.18, 0.80);                                             // 0..1 drives the stagger
      const cap = p < 0.12 ? -1 : p < 0.40 ? 0 : p < 0.62 ? 1 : p < 0.86 ? 2 : 3;
      return { part, yaw, pitch, grip, hold, zoom: 1 - (window.innerWidth < 900 ? 0.12 : 0.04) * hold, spread: sep, flash: Math.max(0, 1 - Math.abs(p - 0.625) / 0.025) * 0.22, cap, pD };
    }

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cw = canvas.clientWidth, ch = canvas.clientHeight;
      if (cw * dpr !== w || ch * dpr !== h) { w = canvas.width = Math.round(cw * dpr); h = canvas.height = Math.round(ch * dpr); }
    }

    const caps = [...root.querySelectorAll('[data-fp3d-cap]')];
    const intro = root.querySelector('[data-fp3d-intro]');
    const bar = root.querySelector('[data-fp3d-bar]');
    let lastCap = -1;

    function draw(now) {
      raf = 0;
      resize();
      shown += (target - shown) * (reduce ? 1 : 0.14);
      if (Math.abs(target - shown) < 1e-4) shown = target;
      const P = reduce ? 1 : shown;
      const S = pose(P, now - t0);

      if (S.cap !== lastCap) { caps.forEach((c, i) => c.classList.toggle('is-on', i === S.cap)); lastCap = S.cap; }
      if (intro) { const k = seg(P, 0.0, 0.12); intro.style.opacity = String(1 - k); intro.style.transform = 'translateY(' + (-40 * k) + 'px)'; intro.style.pointerEvents = k > 0.5 ? 'none' : ''; }
      if (bar) bar.style.transform = 'scaleX(' + P.toFixed(4) + ')';

      const aspect = w / h;
      const narrow = aspect < 0.9;
      const dist = narrow ? 1500 / Math.max(aspect, 0.5) * 0.55 : 840;
      const camY = narrow ? 120 : 150;
      const back = 1 + S.spread * (narrow ? 0.45 : 0.30);
      const eye = [0, camY * S.zoom * back, dist * S.zoom * back];
      const proj = M.persp(narrow ? 0.62 : 0.52, aspect, 10, 5000);
      const heroK = 1 - ease(seg(P, 0.02, 0.22));             // 1 while the hero copy is up
      const wide = aspect > 1.15 && w > 900 * Math.min(window.devicePixelRatio || 1, 2);
      proj[8] -= (wide ? 0.36 : 0) * heroK;                    // shift right on desktop
      proj[9] += (wide ? 0 : 0.30) * heroK;                    // shift down on phones/tablets
      const view = M.lookAt(eye, [0, -10, 0], [0, 1, 0]);
      const VP = M.mul(proj, view);

      const world = M.mul(M.rotX(S.pitch), M.rotY(S.yaw));
      const partM = S.part.map(o => M.mul(world, M.mul(M.trans(o.x, o.y, o.z), M.rotZ(o.rz))));
      const gripM = grips.map((g, i) => {
        const k = clamp((S.grip - g.order * 0.048) / 0.46, 0, 1); // staggered press-in (last one seats at grip = 1)
        const out = (1 - easeOut(k)) * 34;
        const T = M.trans(g.base[0] + g.n[0]*out, g.base[1] + g.n[1]*out, g.base[2] + g.n[2]*out);
        return { m: M.mul(partM[g.part], M.mul(T, g.R)), vis: k > 0 || P > 0.86 };
      });
      // the layer axis: model Z → display Z, rotated with the parts
      const lz = [world[8], world[9], world[10]];

      // shadow pass
      gl.bindFramebuffer(gl.FRAMEBUFFER, sfb); gl.viewport(0, 0, SM, SM);
      gl.clear(gl.DEPTH_BUFFER_BIT); gl.enable(gl.DEPTH_TEST); gl.disable(gl.BLEND);
      gl.enable(gl.POLYGON_OFFSET_FILL); gl.polygonOffset(2, 4);
      gl.useProgram(sprog.p); gl.uniformMatrix4fv(sprog.u.uLightVP, false, lightVP);
      meshes.forEach((me, i) => { gl.uniformMatrix4fv(sprog.u.uModel, false, partM[i]); gl.bindVertexArray(me.s); gl.drawArrays(gl.TRIANGLES, 0, me.m.count); });
      gl.bindVertexArray(gripS);
      gripM.forEach(g => { if (!g.vis) return; gl.uniformMatrix4fv(sprog.u.uModel, false, g.m); gl.drawArrays(gl.TRIANGLES, 0, grip.count); });
      gl.disable(gl.POLYGON_OFFSET_FILL);

      // main pass
      gl.bindFramebuffer(gl.FRAMEBUFFER, null); gl.viewport(0, 0, w, h);
      gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

      // ground shadow
      gl.useProgram(gprog.p); gl.depthMask(false); gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      gl.uniformMatrix4fv(gprog.u.uVP, false, VP); gl.uniformMatrix4fv(gprog.u.uLightVP, false, lightVP);
      gl.uniform1f(gprog.u.uY, -150); gl.uniform1f(gprog.u.uSize, 420); gl.uniform1f(gprog.u.uFade, 1.0);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, stex); gl.uniform1i(gprog.u.uShadow, 0);
      gl.bindVertexArray(quad); gl.drawArrays(gl.TRIANGLES, 0, 6);
      gl.depthMask(true); gl.disable(gl.BLEND);

      gl.useProgram(prog.p);
      const U = prog.u;
      gl.uniformMatrix4fv(U.uVP, false, VP); gl.uniformMatrix4fv(U.uLightVP, false, lightVP);
      gl.uniform3fv(U.uCam, eye);
      gl.uniform3fv(U.uKeyDir, KEY); gl.uniform3fv(U.uKeyCol, [3.6, 3.5, 3.35]);
      gl.uniform3fv(U.uRim1Dir, [-0.9, 0.35, -0.55]); gl.uniform3fv(U.uRim1Col, [0.62, 0.38, 1.45]);   // purple
      gl.uniform3fv(U.uRim2Dir, [0.95, 0.15, -0.45]); gl.uniform3fv(U.uRim2Col, [0.24, 0.70, 1.30]);   // blue
      gl.uniform3fv(U.uLayerAxis, lz);
      // soft fill from just below the camera; grows while the underside is shown
      const fl = 0.35 + 1.25 * S.hold;
      gl.uniform3fv(U.uFillDir, [0.15, -0.25 - 0.3*S.hold, 1.0]); gl.uniform3fv(U.uFillCol, [fl, fl, fl*1.04]);
      gl.uniform1i(U.uShadow, 0);

      // stand: black PLA, satin with layer-line sheen
      gl.uniform3fv(U.uBase, [0.072, 0.073, 0.080]); gl.uniform1f(U.uRough, 0.42); gl.uniform1f(U.uAniso, 0.85);
      gl.uniform1f(U.uSpec, 0.045); gl.uniform1f(U.uGrip, 0);
      meshes.forEach((me, i) => { gl.uniform1f(U.uFlash, S.flash * 0.25); gl.uniformMatrix4fv(U.uModel, false, partM[i]); gl.bindVertexArray(me.v); gl.drawArrays(gl.TRIANGLES, 0, me.m.count); });

      // grips: soft matte rubber, a touch lighter so they read against the PLA
      gl.uniform3fv(U.uBase, [0.018, 0.018, 0.020]); gl.uniform1f(U.uRough, 0.62); gl.uniform1f(U.uAniso, 0.0);
      gl.uniform1f(U.uSpec, 0.03); gl.uniform1f(U.uFlash, 0); gl.uniform1f(U.uGrip, 1);
      gl.bindVertexArray(gripV);
      gripM.forEach(g => { if (!g.vis) return; gl.uniformMatrix4fv(U.uModel, false, g.m); gl.drawArrays(gl.TRIANGLES, 0, grip.count); });
      gl.bindVertexArray(null);

      if (Math.abs(target - shown) > 1e-4) schedule();
    }
    function schedule() { if (!raf && visible) raf = requestAnimationFrame(draw); }

    let visible = true;
    new IntersectionObserver(es => { visible = es[0].isIntersecting; if (visible) schedule(); }, { rootMargin: '100px' }).observe(root);
    // Horizon scrolls an inner .page-wrapper on desktop (html/body are locked), so listen in the capture
    // phase on document: that catches scrolling of the window and of any inner scroll container.
    document.addEventListener('scroll', () => { target = progress(); schedule(); }, { passive: true, capture: true });
    window.addEventListener('resize', () => { target = progress(); schedule(); });
    target = shown = progress();
    root.classList.add('fp3d--ready');
    schedule();
    // test hook
    root.fp3d = { setProgress(p) { target = shown = p; draw(performance.now()); } };
  }

  function boot() { document.querySelectorAll('[data-fp3d]').forEach(r => init(r).catch(e => { console.error('fp3d', e); r.classList.add('fp3d--nogl'); })); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
